import React, { useState, useEffect, useCallback } from "react";
import { formStepsFlat as steps } from "../../constants";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NavLink } from "react-router-dom";
import Loading from "../components/Loading";
import api from "../api";
import { trackButtonClick } from "../../providers/TagTracking";

const LandingPage = () => {
  // Initial full form state with 23 questions
  const initialState = React.useMemo(
    () => ({
      company: "",
      name: "",
      email: "",
      phone: "",
      brandMake: "",
      model: "",
      quantity: "",
      rebuildOnSite: "",
      whoRebuild: "",
      primaryUser: "",
      hoursGotIt: "",
      mainIssue: "",
      oemPartNumber: "",
      interestedInNewMaterial: "",
      needExpertOpinion: "",
      openToNewApplication: "",
      wetOrDryApplication: "",
      hoursOfDowntime: "",
      amountLossByThroughputHr: "",
      mattersMost: "",
      shiftsPerDay: "",
      weeksPerMonth: 4.2,
      monthsPerYear: 12,
      costExistingPart: "",
      partsPerEquipment: "",
      operativeDaysPerWeek: 5,
    }),
    []
  );

  // State management
  const [formData, setFormData] = useState(initialState);
  const [currentStep, setCurrentStep] = useState(0); // 0 .. steps.length-1 for form steps
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const [flashVisible, setFlashVisible] = useState(false);
  const [totalSavings, setTotalSavings] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const sessionIdRef = React.useRef(null);

  // Calculate savings based on form data
  const calculateSavings = useCallback(() => {
    const {
      quantity,
      partsPerEquipment,
      costExistingPart,
      hoursGotIt,
      hoursOfDowntime,
      amountLossByThroughputHr,
      shiftsPerDay,
      weeksPerMonth = 4.2,
      monthsPerYear = 12,
      operativeDaysPerWeek = 5,
    } = formData;

    // Convert string values to numbers
    const qty = parseInt(quantity) || 0;
    const partsPerEquip = parseInt(partsPerEquipment) || 0;
    const costPart = parseFloat(costExistingPart) || 0;
    const hours = parseFloat(hoursGotIt) || 0;
    const downtime = parseFloat(hoursOfDowntime) || 0;
    const lossPerHour = parseFloat(amountLossByThroughputHr) || 0;
    const shifts = parseInt(shiftsPerDay) || 0;
    const weeksPerMo = parseFloat(weeksPerMonth) || 4.2;
    const monthsPerYr = parseFloat(monthsPerYear) || 12;
    const daysPerWeek = parseFloat(operativeDaysPerWeek) || 5;

    // Basic validation - if any required field is missing or 0, return 0
    if (!qty || !partsPerEquip || !shifts) {
      return 0;
    }

    // Special case: if hoursGotIt is 0, we can't calculate meaningful savings
    if (hours === 0) {
      return 0;
    }

    try {
      // Calculate total savings (simplified calculation)
      // This is a placeholder calculation - you should replace with your actual formula
      const annualOperatingHours =
        shifts * 8 * daysPerWeek * weeksPerMo * monthsPerYr;
      const partsUsedPerYear =
        (annualOperatingHours / hours) * qty * partsPerEquip;
      const downtimeCostPerYear =
        (annualOperatingHours / hours) * downtime * lossPerHour * qty;
      const partsCostPerYear = partsUsedPerYear * costPart;

      // Assume 20% savings on parts cost and 50% reduction in downtime
      const partsSavings = partsCostPerYear * 0.2;
      const downtimeSavings = downtimeCostPerYear * 0.5;

      const totalSavings = partsSavings + downtimeSavings;

      // Ensure result is a valid number
      if (isNaN(totalSavings) || !isFinite(totalSavings)) {
        return 0;
      }

      return Math.max(0, Math.round(totalSavings));
    } catch (error) {
      console.error("Error calculating savings:", error);
      return 0;
    }
  }, [formData]);

  // Generate sessionId only if needed
  const getSessionId = () => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = `session_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
    }
    return sessionIdRef.current;
  };

  // On page unload, if not submitted, save as unsubmitted lead
  React.useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (!formSubmitted && hasStarted) {
        const sessionId = getSessionId();
        const dataToSave = {
          ...formData,
          sessionId,
          currentStep,
        };
        try {
          await api.createOrUpdateUnsubmittedLead(dataToSave);
        } catch (e) {
          // ignore
        }
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [formData, currentStep, formSubmitted, hasStarted]);

  // Handle change events for all fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    // If the first field (quantity) is being filled for the first time, enable auto-save
    if (name === "quantity" && !hasStarted && value.trim() !== "") {
      setHasStarted(true);
    }

    // Clear any existing error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Note: Real-time validation removed - validation now happens only on Next/Submit button click

    // Check if this is a left side field being filled in the split layout
    const leftSideFields = [
      "company",
      "phone",
      "name",
      "brandMake",
      "model",
      "rebuildOnSite",
      "whoRebuild",
      "primaryUser",
      "wetOrDryApplication",
      "mainIssue",
      "oemPartNumber",
      "interestedInNewMaterial",
      "needExpertOpinion",
      "mattersMost",
    ];

    // If user is filling a left side field and it has a meaningful value, auto-set openToNewApplication to "No"
    if (leftSideFields.includes(name) && value && value.trim() !== "") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        openToNewApplication: "No", // Automatically switch to No when filling left side
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Special handling for "None of the above, I have a new application for you"
    if (name === "openToNewApplication" && value === "Yes") {
      // Set default values only for empty fields - preserve existing financial data
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        // Only set defaults for empty or undefined fields, preserve existing financial data
        company: prev.company || "New Application Company",
        name: prev.name || "New Application Customer",
        primaryUser: prev.primaryUser || "I am the primary user",
        mainIssue: prev.mainIssue || "Low Hours",
        wetOrDryApplication: prev.wetOrDryApplication || "Dry",
        mattersMost: prev.mattersMost || "Durability",
        // Don't override financial calculation fields - preserve user input
        // quantity: preserve existing value
        // hoursGotIt: preserve existing value
        // hoursOfDowntime: preserve existing value
        // amountLossByThroughputHr: preserve existing value
        // shiftsPerDay: preserve existing value
        // costExistingPart: preserve existing value
        // partsPerEquipment: preserve existing value
        // operativeDaysPerWeek: preserve existing value
        // weeksPerMonth: preserve existing value
        // monthsPerYear: preserve existing value
      }));
      // Continue with normal flow to collect email and other info
    }
  };

  // Submit the full form data
  const submitForm = useCallback(async () => {
    setLoading(true);
    setProgress(60);
    setLoadingMessage("Preparing submission...");

    try {
      // Simulate some processing time at 60%
      await new Promise((resolve) => setTimeout(resolve, 500));

      setProgress(80);
      setLoadingMessage("Submitting form...");

      // Convert Yes/No strings to booleans and string numbers to actual numbers
      // For new applications, ensure required fields are always included
      const isNewApplication = formData.openToNewApplication === "Yes";
      const requiredFieldsForNewApp = [
        "company",
        "name",
        "email",
        "brandMake",
        "model",
        "primaryUser",
        "mainIssue",
        "wetOrDryApplication",
        "mattersMost",
      ];

      // Email and phone should always be included regardless of new application status
      // But phone should be excluded if empty (since it's optional)
      // Always include default calculation fields
      const alwaysIncludeFields = [
        "company",
        "email",
        "weeksPerMonth",
        "monthsPerYear",
        "operativeDaysPerWeek",
      ];

      const processedFormData = Object.fromEntries(
        Object.entries(formData)
          .filter(([key, value]) => {
            // Always include default calculation fields and basic required fields
            if (alwaysIncludeFields.includes(key)) {
              return true;
            }
            // Include phone only if it has a value
            if (key === "phone") {
              return value && value.trim() !== "";
            }
            // Always include required fields for new applications, even if empty
            if (isNewApplication && requiredFieldsForNewApp.includes(key)) {
              return true;
            }
            // For other fields, filter out empty values
            return value !== "" && value !== undefined;
          })
          .map(([key, value]) => {
            // Convert Yes/No strings to booleans for these specific fields
            if (
              [
                "rebuildOnSite",
                "interestedInNewMaterial",
                "needExpertOpinion",
                "openToNewApplication",
              ].includes(key)
            ) {
              return [key, value === "Yes"];
            }

            // Convert string numbers to actual numbers for numeric fields
            if (
              [
                "quantity",
                "hoursGotIt",
                "hoursOfDowntime",
                "amountLossByThroughputHr",
                "shiftsPerDay",
                "weeksPerMonth",
                "monthsPerYear",
                "costExistingPart",
                "partsPerEquipment",
                "operativeDaysPerWeek",
              ].includes(key)
            ) {
              // For new applications, allow null values to pass through
              if (value === null) {
                return [key, null];
              }

              const numValue = parseFloat(value);
              // For new applications, keep invalid numbers as null instead of defaults
              if (isNaN(numValue)) {
                if (formData.openToNewApplication === "Yes") {
                  return [key, null];
                }
                return [key, initialState[key] || 0];
              }
              return [key, numValue];
            }

            // Handle empty name field for new applications
            if (key === "name" && isNewApplication) {
              // Ensure name has minimum 3 characters for validation
              if (!value || value.trim().length < 3) {
                return [key, "New Application Customer"];
              }
            }

            return [key, value];
          })
      );

      // Add sessionId to track conversion from unsubmitted lead
      const sessionId = getSessionId();
      if (sessionId) {
        processedFormData.sessionId = sessionId;
      }

      const result = await api.createLead(processedFormData);

      setProgress(90);
      setLoadingMessage("Processing response...");

      setProgress(100);
      setLoadingMessage("Complete!");

      // Small delay to show 100% completion
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Don't show generic success toast - only show server response or savings info
      // toast.success removed to reduce noise

      // Handle different possible response structures
      let savings = null;
      if (result && result.data && result.data.totalSavings !== undefined) {
        savings = result.data.totalSavings;
      } else if (result && result.totalSavings !== undefined) {
        savings = result.totalSavings;
      } else if (result && typeof result === "object") {
        // Try to find totalSavings anywhere in the response
        const findSavings = (obj) => {
          for (const key in obj) {
            if (key === "totalSavings") return obj[key];
            if (typeof obj[key] === "object" && obj[key] !== null) {
              const found = findSavings(obj[key]);
              if (found !== undefined) return found;
            }
          }
          return undefined;
        };
        savings = findSavings(result);
      }

      // Display API response information to user
      if (result && result.message) {
        toast.info(`Server Response: ${result.message}`, {
          autoClose: 4000,
        });
      }

      if (savings !== null && savings !== undefined) {
        setTotalSavings(savings);
        setFlashVisible(true);
        // Don't show toast for savings - only show in modal
      } else {
        // If no savings were returned from the backend, try frontend calculation
        const frontendSavings = calculateSavings();
        if (frontendSavings && frontendSavings > 0) {
          setTotalSavings(frontendSavings);
          setFlashVisible(true);
          // Don't show toast for frontend savings - only show in modal
        } else if (formData.openToNewApplication === "Yes") {
          // For new applications without calculation, show neutral message
          setFlashVisible(true);
          // Don't show toast for new applications - only show in modal
        } else {
          // Don't show warning toast - let the modal handle this
          setFlashVisible(true);
        }
      }

      setFormSubmitted(true); // Mark as submitted so unload won't save
    } catch (error) {
      // Extract detailed error information for better user feedback
      let errorMessage = error.message || "An unexpected error occurred";
      let errorType = "Unknown Error";

      // Handle different types of errors with specific messages and icons
      if (
        error.message.includes("validation failed") ||
        error.message.includes("Validation Error") ||
        error.message.includes("Lead validation failed")
      ) {
        errorType = "Validation Error";
        // Extract the specific validation error message
        if (error.message.includes(":")) {
          const validationMessage = error.message
            .split(":")
            .slice(1)
            .join(":")
            .trim();
          errorMessage = validationMessage;
        }
        toast.error(`❌ ${errorType}: ${errorMessage}`, {
          autoClose: 10000,
        });
      } else if (error.message.includes("must be between")) {
        errorType = "Input Range Error";
        toast.error(`📊 ${errorType}: ${errorMessage}`, {
          autoClose: 8000,
        });
      } else if (
        error.message.includes("must be at least") ||
        error.message.includes("minimum")
      ) {
        errorType = "Minimum Value Error";
        toast.error(`📈 ${errorType}: ${errorMessage}`, {
          autoClose: 8000,
        });
      } else if (error.message.includes("must be a positive number")) {
        errorType = "Number Format Error";
        toast.error(`🔢 ${errorType}: ${errorMessage}`, {
          autoClose: 8000,
        });
      } else if (
        error.message.includes("Email") ||
        error.message.includes("email")
      ) {
        errorType = "Email Error";
        toast.error(`📧 ${errorType}: ${errorMessage}`, {
          autoClose: 8000,
        });
      } else if (error.message.includes("required")) {
        errorType = "Required Field Error";
        toast.error(`⚠️ ${errorType}: ${errorMessage}`, {
          autoClose: 8000,
        });
      } else if (
        error.message.includes("already exists") ||
        error.message.includes("conflict") ||
        error.message.includes("duplicate")
      ) {
        errorType = "Conflict Error";
        toast.error(`🚫 ${errorType}: ${errorMessage}`, {
          autoClose: 8000,
        });
      } else if (
        error.message.includes("rate limit") ||
        error.message.includes("too many")
      ) {
        errorType = "Rate Limit Error";
        toast.error(
          `🚦 ${errorType}: Too many requests. Please wait and try again.`,
          {
            autoClose: 10000,
          }
        );
      } else if (
        error.message.includes("forbidden") ||
        error.message.includes("access denied")
      ) {
        errorType = "Access Error";
        toast.error(`🚫 ${errorType}: Access denied. Please contact support.`, {
          autoClose: 10000,
        });
      } else if (
        error.message.includes("Network") ||
        error.message.includes("fetch") ||
        error.message.includes("Failed to fetch")
      ) {
        errorType = "Network Error";
        errorMessage =
          "Unable to connect to server. Please check your internet connection and try again.";
        toast.error(`🌐 ${errorType}: ${errorMessage}`, {
          autoClose: 10000,
        });
      } else if (
        error.message.includes("Session expired") ||
        error.message.includes("unauthorized")
      ) {
        errorType = "Authentication Error";
        toast.error(`🔐 ${errorType}: ${errorMessage}`, {
          autoClose: 8000,
        });
      } else if (
        error.message.includes("Server Error") ||
        error.message.includes("Internal")
      ) {
        errorType = "Server Error";
        errorMessage =
          "The server encountered an error. Our team has been notified. Please try again later.";
        toast.error(`🚨 ${errorType}: ${errorMessage}`, {
          autoClose: 10000,
        });
      } else if (error.message.includes("timeout")) {
        errorType = "Timeout Error";
        errorMessage =
          "The request took too long to complete. Please try again.";
        toast.error(`⏰ ${errorType}: ${errorMessage}`, {
          autoClose: 8000,
        });
      } else {
        // Generic error with fallback icon
        toast.error(`❌ ${errorType}: ${errorMessage}`, {
          autoClose: 10000,
        });
      }

      // Note: Instruction toast removed to reduce noise
    } finally {
      setLoading(false);
      setProgress(0);
      setLoadingMessage("Loading...");
    }
  }, [calculateSavings, formData, initialState]);

  // Move to the next step (or, if at the last step, submit the form)
  const handleNext = useCallback(async () => {
    // If we're still in the question steps, validate the current step if required.
    if (currentStep < steps.length) {
      const step = steps[currentStep];
      let hasError = false;
      const newErrors = {};

      if (step.type === "multi") {
        // Validate each field in the multi-field step
        step.fields.forEach((field) => {
          if (field.required) {
            const error = field.validate(formData[field.name]);
            if (error) {
              newErrors[field.name] = error;
              hasError = true;
            }
          }
        });

        // Also check if the step itself has validation
        if (step.required && step.validate) {
          const stepError = step.validate(formData);
          if (stepError) {
            newErrors[step.name] = stepError;
            hasError = true;
          }
        }
      } else if (step.type === "split") {
        // Special validation for split layout
        const isNewApplication = formData.openToNewApplication === "Yes";

        if (isNewApplication) {
          // If new application selected, only validate minimal fields
          const requiredForNewApp = ["company", "name"];
          for (const fieldName of requiredForNewApp) {
            if (!formData[fieldName] || formData[fieldName].trim() === "") {
              newErrors[fieldName] = `${
                fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
              } is required for new applications`;
              hasError = true;
            }
          }
        } else {
          // If not new application, validate ALL left side required fields
          step.leftSide.forEach((field) => {
            if (field.required) {
              const error = field.validate(formData[field.name]);
              if (error) {
                newErrors[field.name] = error;
                hasError = true;
              }
            }
          });

          // Ensure they've made a choice and it's "No" when filling left side
          if (formData.openToNewApplication !== "No") {
            // Auto-set to "No" if they're filling left side fields but haven't selected
            const hasLeftSideData = step.leftSide.some(
              (field) =>
                formData[field.name] &&
                formData[field.name].toString().trim() !== ""
            );
            if (hasLeftSideData && !formData.openToNewApplication) {
              // This will be handled by the handleChange function automatically
            }
          }
        }
      } else {
        // Standard field validation - validate ALL required fields
        if (step.required) {
          const error = step.validate ? step.validate(formData[step.name]) : "";
          if (error) {
            newErrors[step.name] = error;
            hasError = true;
          }
          // Additional check for empty required fields
          if (
            !formData[step.name] ||
            (typeof formData[step.name] === "string" &&
              formData[step.name].trim() === "")
          ) {
            newErrors[step.name] = `${step.label} is required`;
            hasError = true;
          }
        }
      }

      if (hasError) {
        setErrors(newErrors);
        // Note: Validation error toasts removed - errors shown only in form UI
        return;
      }

      // Email validation step - check if we're on the email step
      if (step.name === "email") {
        const email = formData.email;
        if (email && email.trim()) {
          setLoading(true);
          setProgress(20);
          setLoadingMessage("Validating email address...");

          try {
            const validationResult = await api.validateEmail(email.trim());

            setProgress(60);
            setLoadingMessage("Processing validation result...");

            if (!validationResult.valid) {
              setLoading(false);
              setProgress(0);
              setLoadingMessage("Loading...");

              // Set email validation error
              newErrors.email =
                validationResult.message || "Please enter a valid email inbox";
              setErrors(newErrors);

              // Show specific error message with suggestion if available
              if (validationResult.details?.suggestion) {
                const suggestion = validationResult.details.suggestion;
                toast.error(
                  <div>
                    <div>📧 {validationResult.message}</div>
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "0.9em",
                        color: "#666",
                      }}
                    >
                      💡 Did you mean: <strong>{suggestion}</strong>?
                    </div>
                  </div>,
                  {
                    autoClose: 8000,
                    closeOnClick: true,
                  }
                );
              } else {
                toast.error(
                  `📧 Email Validation Error: ${
                    validationResult.message ||
                    "Please enter a valid email inbox that can receive emails"
                  }`,
                  {
                    autoClose: 5000,
                  }
                );
              }
              return;
            }

            setProgress(100);
            setLoadingMessage("Email validated successfully!");

            // Small delay to show success
            await new Promise((resolve) => setTimeout(resolve, 800));

            setLoading(false);
            setProgress(0);
            setLoadingMessage("Loading...");

            // Track conversion after successful email validation
            trackButtonClick();

            // Don't show email verification success toast
          } catch (error) {
            setLoading(false);
            setProgress(0);
            setLoadingMessage("Loading...");

            // For validation service errors, we'll allow them to proceed but show a warning
            toast.warn(
              "⚠️ Email validation service temporarily unavailable. Proceeding with basic format validation.",
              {
                autoClose: 5000,
              }
            );

            // Fall back to basic email format validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email.trim())) {
              newErrors.email = "Please enter a valid email format";
              setErrors(newErrors);
              toast.error("📧 Please enter a valid email format", {
                autoClose: 4000,
              });
              return;
            }

            // Don't show fallback success message toast
          }
        }
      }

      setErrors({});

      // Calculate and show savings after financial info step
      const currentStepData = steps[currentStep];
      if (currentStepData.name === "financialInfo") {
        // Calculate savings
        const savings = calculateSavings();
        setTotalSavings(savings);
        // Note: No longer showing savings toast to avoid revealing the amount
      }

      // Check if this is the last step - if so, submit the form
      if (currentStep === steps.length - 1) {
        // This is the last step, submit the form directly
        submitForm();
      } else {
        // Move to next step
        setCurrentStep(currentStep + 1);
      }
    }
  }, [currentStep, formData, submitForm, calculateSavings]);

  // Allow user to go back one step
  const handleBack = useCallback(() => {
    if (currentStep > 0 && !loading) {
      setErrors({});

      // Special handling for new applications: if we're on email/phone and it's a new application,
      // go back to Additional Questions instead of the immediate previous step
      const currentStepData = steps[currentStep];
      if (
        (currentStepData?.name === "email" ||
          currentStepData?.name === "phone") &&
        formData.openToNewApplication === "Yes"
      ) {
        const additionalQuestionsIndex = steps.findIndex(
          (step) => step.name === "additionalQuestions"
        );
        if (additionalQuestionsIndex !== -1) {
          setCurrentStep(additionalQuestionsIndex);
          return;
        }
      }

      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, formData.openToNewApplication, loading]);

  // Remove auto-hide timer - congratulations modal should stay until action is taken
  // useEffect(() => {
  //   if (flashVisible) {
  //     const timer = setTimeout(() => setFlashVisible(false), 12000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [flashVisible]);

  // Add keyboard event listener for Enter and Escape keys
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !loading) {
        e.preventDefault();
        if (flashVisible) {
          // If congratulations modal is visible, Enter does nothing (user must click action buttons)
          return;
        }
        handleNext();
      } else if (e.key === "Escape" && !loading) {
        e.preventDefault();
        if (flashVisible) {
          // If congratulations modal is visible, Escape does nothing (user must click action buttons)
          return;
        } else {
          // Otherwise, go back in the form
          handleBack();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleNext, handleBack, loading, flashVisible]); // Added flashVisible dependency

  // Auto-focus on the current input field when step changes
  useEffect(() => {
    if (currentStep < steps.length && !loading && !flashVisible) {
      // Small delay to ensure the DOM is updated
      const timer = setTimeout(() => {
        const currentStepData = steps[currentStep];

        if (currentStepData.type === "multi") {
          // For multi-field steps, focus on the first field
          const firstField = currentStepData.fields[0];
          if (firstField) {
            const firstInput = document.querySelector(
              `[name="${firstField.name}"]`
            );
            if (firstInput) {
              firstInput.focus();
            }
          }
        } else {
          // For single field steps, focus on the main input
          const currentInput = document.querySelector(
            `[name="${currentStepData.name}"]`
          );
          if (currentInput) {
            currentInput.focus();
            // For text inputs, move cursor to end (but not for email type)
            if (currentInput.type === "text" || currentInput.type === "tel") {
              const valueLength = currentInput.value.length;
              currentInput.setSelectionRange(valueLength, valueLength);
            }
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [currentStep, loading, flashVisible]);

  // When starting a new form, reset formSubmitted and sessionId
  const handleNewForm = () => {
    setFlashVisible(false);
    setTotalSavings(null);
    setFormData(initialState);
    setCurrentStep(0);
    setErrors({});
    setFormSubmitted(false);
  };

  if (loading) {
    return (
      <Loading currentPage={"/"} progress={progress} message={loadingMessage} />
    );
  }

  // Render the current form step
  return (
    <>
      <NavLink to="/login" className="fixed top-4 right-4 group">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 text-gray-900 cursor-pointer"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
        <span className="absolute top-full right-0 mt-2 hidden group-hover:block bg-gray-900 text-white text-xs p-1 rounded">
          Login
        </span>
      </NavLink>

      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        {/* Tribco Logo */}
        <div className="mb-4">
          <img
            src="/tribco-logo.png"
            alt="Tribco Inc. Logo"
            className="h-16 w-auto mx-auto"
          />
        </div>
        <h2 className="text-3xl font-semibold text-gray-800 mb-6">
          Tribco's Savings Calculator
        </h2>
        <div className="w-full max-w-3xl bg-white rounded-xl shadow-lg p-8 relative">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progress
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gray-700 h-2 rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${
                    // Simplified progress bar: 0% on step 0, 50% on step 1, 100% on step 2
                    currentStep === 0 ? 0 : currentStep === 1 ? 50 : 100
                  }%`,
                }}
              ></div>
            </div>
          </div>

          <div className="mb-4">
            {steps[currentStep].type === "multi" ? (
              <div className="space-y-6">
                {steps[currentStep].fields.map((field, fieldIndex) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}{" "}
                      {field.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    {field.type === "select" ? (
                      <select
                        name={field.name}
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        className={`block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                          errors[field.name] ? "border-red-500" : ""
                        }`}
                      >
                        <option value="">{field.placeholder}</option>
                        {field.options.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : field.type === "radio" ? (
                      <div className="flex space-x-4 mt-2">
                        {field.options.map((option) => (
                          <label key={option} className="flex items-center">
                            <input
                              type="radio"
                              name={field.name}
                              value={option}
                              checked={formData[field.name] === option}
                              onChange={handleChange}
                              className="h-4 w-4 accent-gray-900 border-gray-300 mr-2"
                            />
                            <span className="text-sm text-gray-700">
                              {option}
                            </span>
                          </label>
                        ))}
                      </div>
                    ) : field.type === "textarea" ? (
                      <textarea
                        name={field.name}
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        className={`block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                          errors[field.name] ? "border-red-500" : ""
                        }`}
                        rows={3}
                      />
                    ) : (
                      <input
                        name={field.name}
                        type={field.type}
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        className={`block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                          errors[field.name] ? "border-red-500" : ""
                        }`}
                        step={field.step || undefined}
                        min={field.min || undefined}
                        max={field.max || undefined}
                      />
                    )}
                    {errors[field.name] && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors[field.name]}
                      </p>
                    )}
                  </div>
                ))}
                {/* Display step-level validation error for multi-type fields */}
                {errors[steps[currentStep].name] && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">
                      {errors[steps[currentStep].name]}
                    </p>
                  </div>
                )}
              </div>
            ) : steps[currentStep].type === "split" ? (
              // Split layout rendering
              <div>
                {/* Almost there message */}
                <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg text-center">
                  <h3 className="text-xl font-bold text-yellow-800 mb-2">
                    🎯 Almost there!!!
                  </h3>
                  <p className="text-yellow-700 font-medium">
                    Your savings estimate is on the next page.
                  </p>
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-6 text-center">
                  {steps[currentStep].label}
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Side */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-700 mb-4 border-b pb-2">
                      Company & Equipment Details
                    </h4>
                    {steps[currentStep].leftSide.map((field) => (
                      <div key={field.name}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}{" "}
                          {field.required && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        {field.type === "select" ? (
                          <select
                            name={field.name}
                            value={formData[field.name] || ""}
                            onChange={handleChange}
                            className={`block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                              errors[field.name] ? "border-red-500" : ""
                            }`}
                          >
                            <option value="">{field.placeholder}</option>
                            {field.options.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : field.type === "radio" ? (
                          <div className="flex space-x-4 mt-2">
                            {field.options.map((option) => (
                              <label key={option} className="flex items-center">
                                <input
                                  type="radio"
                                  name={field.name}
                                  value={option}
                                  checked={formData[field.name] === option}
                                  onChange={handleChange}
                                  className="h-4 w-4 accent-gray-900 border-gray-300 mr-2"
                                />
                                <span className="text-sm text-gray-700">
                                  {option}
                                </span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <input
                            name={field.name}
                            type={field.type}
                            value={formData[field.name] || ""}
                            onChange={handleChange}
                            placeholder={field.placeholder}
                            className={`block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                              errors[field.name] ? "border-red-500" : ""
                            }`}
                          />
                        )}
                        {errors[field.name] && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors[field.name]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Right Side */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-700 mb-4 border-b pb-2">
                      Alternative Option
                    </h4>
                    <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                      {steps[currentStep].rightSide.map((field) => (
                        <div key={field.name}>
                          <label className="block text-sm font-medium text-gray-800 mb-2">
                            {field.label}
                          </label>
                          <div className="flex space-x-4 mt-2">
                            {field.options.map((option) => (
                              <label key={option} className="flex items-center">
                                <input
                                  type="radio"
                                  name={field.name}
                                  value={option}
                                  checked={formData[field.name] === option}
                                  onChange={handleChange}
                                  className="h-4 w-4 accent-gray-700 border-gray-300 mr-2"
                                />
                                <span className="text-sm text-gray-700 font-medium">
                                  {option}
                                </span>
                              </label>
                            ))}
                          </div>
                          {errors[field.name] && (
                            <p className="mt-1 text-xs text-red-500">
                              {errors[field.name]}
                            </p>
                          )}
                        </div>
                      ))}
                      <p className="text-xs text-gray-600 mt-3">
                        💡 Select "Yes" if you have a new application that
                        doesn't fit the standard questions above.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Display step-level validation error for split layout */}
                {errors[steps[currentStep].name] && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">
                      {errors[steps[currentStep].name]}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Savings display for email step */}
                {steps[currentStep].name === "email" && (
                  <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg text-center">
                    <h3 className="text-lg font-semibold text-green-800 mb-2">
                      🎉 Congratulations!
                    </h3>
                    <p className="text-gray-700 mb-2">
                      Tribco's friction products can save you up to
                    </p>
                    <div className="text-3xl font-bold text-green-600 mb-2 relative">
                      {/* Always show blurred savings on email step */}
                      <span style={{ filter: "blur(3px)", userSelect: "none" }}>
                        {totalSavings && totalSavings > 0
                          ? `$${totalSavings.toLocaleString("en-US")}`
                          : "$125,000"}
                      </span>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative">
                          {/* Background blur effect */}
                          <div
                            className="absolute inset-0 bg-green-50 rounded-lg opacity-90"
                            style={{ filter: "blur(2px)" }}
                          ></div>
                          {/* Main text */}
                          <span className="relative text-lg font-semibold text-green-800 bg-green-50 px-3 py-2 rounded-lg shadow-sm">
                            For privacy protection, please validate your email
                            below
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Note - This is an estimate not an actual amount, it could
                      be drastically lower or higher than the estimate.
                    </p>
                  </div>
                )}
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {steps[currentStep].label}{" "}
                  {steps[currentStep].required && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                {steps[currentStep].type === "select" ? (
                  <select
                    name={steps[currentStep].name}
                    value={formData[steps[currentStep].name] || ""}
                    onChange={handleChange}
                    className={`block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                      errors[steps[currentStep].name] ? "border-red-500" : ""
                    }`}
                  >
                    <option value="">{steps[currentStep].placeholder}</option>
                    {steps[currentStep].options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : steps[currentStep].type === "radio" ? (
                  <div className="flex space-x-4 mt-2">
                    {steps[currentStep].options.map((option) => (
                      <label key={option} className="flex items-center">
                        <input
                          type="radio"
                          name={steps[currentStep].name}
                          value={option}
                          checked={formData[steps[currentStep].name] === option}
                          onChange={handleChange}
                          className="h-4 w-4 accent-gray-900 border-gray-300 mr-2"
                        />
                        <span className="text-sm text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                ) : steps[currentStep].type === "textarea" ? (
                  <textarea
                    name={steps[currentStep].name}
                    value={formData[steps[currentStep].name] || ""}
                    onChange={handleChange}
                    placeholder={steps[currentStep].placeholder}
                    className={`block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                      errors[steps[currentStep].name] ? "border-red-500" : ""
                    }`}
                    rows={3}
                  />
                ) : (
                  <div className="relative">
                    <input
                      name={steps[currentStep].name}
                      type={steps[currentStep].type}
                      value={formData[steps[currentStep].name] || ""}
                      onChange={handleChange}
                      placeholder={steps[currentStep].placeholder}
                      className={`block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-gray-900 focus:ring-gray-900 sm:text-sm ${
                        errors[steps[currentStep].name] ? "border-red-500" : ""
                      } ${steps[currentStep].name === "email" ? "pr-10" : ""}`}
                      step={steps[currentStep].step || undefined}
                      min={steps[currentStep].min || undefined}
                      max={steps[currentStep].max || undefined}
                    />
                    {/* Email validation indicator */}
                    {steps[currentStep].name === "email" &&
                      formData.email &&
                      formData.email.trim() && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <div className="text-sm text-gray-500">📧</div>
                        </div>
                      )}
                  </div>
                )}
                {/* Email validation help text - moved below the input field */}
                {steps[currentStep].name === "email" && (
                  <p className="text-xs text-gray-500 mt-2 mb-2">
                    For privacy protection, please validate your email below
                  </p>
                )}
                {errors[steps[currentStep].name] && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors[steps[currentStep].name]}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Disclosure text */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500 leading-relaxed text-center">
              By submitting this form, you authorize Tribco Inc., to have all
              kinds of communication with you
            </p>
          </div>

          {flashVisible && totalSavings !== null && (
            <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl mx-4 text-center relative min-h-[400px] flex flex-col justify-center">
                <h2 className="text-4xl font-bold text-green-600 mb-4">
                  🎉 Congrats!
                </h2>

                {formData.openToNewApplication === "Yes" ? (
                  // Content for new applications
                  <>
                    {totalSavings && totalSavings > 0 ? (
                      // Show savings if calculated for new applications
                      <>
                        <p className="text-xl text-gray-700 mb-2">
                          Great news! Based on your data, Tribco's friction
                          products can save you up to
                        </p>

                        <div className="text-6xl font-bold text-green-500 mb-4">
                          {totalSavings.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </div>

                        <p className="text-lg text-gray-700 mb-4">
                          We'll contact you soon to discuss your new application
                          and these potential savings!
                        </p>

                        <p className="text-xs text-gray-500 mb-6 leading-relaxed">
                          Note - This is an estimate not an actual amount, it
                          could be drastically lower or higher than the
                          estimate.
                        </p>
                      </>
                    ) : (
                      // Fallback message when no savings calculated
                      <p className="text-xl text-gray-700 mb-8">
                        Thank you for your submission! We'll contact you soon to
                        discuss your new application.
                      </p>
                    )}

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button
                        onClick={handleNewForm}
                        className="px-8 py-4 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-950 transition-colors text-lg"
                      >
                        Fill New Form
                      </button>

                      <a
                        href="https://calendly.com/charles-tribco/friction-discs-tribco"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-8 py-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-lg"
                      >
                        Schedule a Call
                      </a>
                    </div>

                    {/* Unsubscribe text */}
                    <div className="mt-6">
                      <p className="text-xs text-gray-500">
                        To unsubscribe email us at{" "}
                        <a
                          href="mailto:sales@tribco.com"
                          className="text-gray-600 hover:text-gray-800 underline"
                        >
                          sales@tribco.com
                        </a>
                      </p>
                    </div>
                  </>
                ) : (
                  // Content for regular applications with savings calculation
                  <>
                    <p className="text-xl text-gray-700 mb-2">
                      Tribco's friction products can save you up to
                    </p>

                    <div className="text-6xl font-bold text-green-500 mb-6">
                      {totalSavings.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </div>

                    <p className="text-xs text-gray-500 mb-8 leading-relaxed">
                      Note - This is an estimate not an actual amount, it could
                      be drastically lower or higher than the estimate.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button
                        onClick={handleNewForm}
                        className="px-8 py-4 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-950 transition-colors text-lg"
                      >
                        Fill New Form
                      </button>

                      <a
                        href="https://calendly.com/charles-tribco/friction-discs-tribco"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-8 py-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-lg"
                      >
                        Contact Us
                      </a>
                    </div>

                    {/* Unsubscribe text */}
                    <div className="mt-6">
                      <p className="text-xs text-gray-500">
                        To unsubscribe email us at{" "}
                        <a
                          href="mailto:sales@tribco.com"
                          className="text-gray-600 hover:text-gray-800 underline"
                        >
                          sales@tribco.com
                        </a>
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-between mt-6">
            {currentStep > 0 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={loading}
                className={`py-2 px-4 rounded-md shadow-sm text-sm font-medium focus:outline-none ${
                  loading
                    ? "text-gray-400 bg-gray-200 cursor-not-allowed"
                    : "text-gray-900 bg-gray-300 hover:bg-gray-400"
                }`}
              >
                {loading ? "Please wait..." : "Back"}
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              className="ml-auto py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-950 focus:outline-none"
            >
              {currentStep === steps.length - 1 ? "Submit" : "Next"}
            </button>
          </div>
        </div>
      </div>

      {/* Toast Container for notifications */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        limit={5}
      />
    </>
  );
};

export default LandingPage;
